import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { fetchAssignedWorkoutTree, type AssignedWorkoutTree } from "../lib/assignedWorkoutTree";
import { flattenWeek } from "../lib/nextWeekProposal";
import { compareWeeks, type RowComparison } from "../lib/weekDelta";
import { canHold, groupMoves, holdLabel, holdWrite, rowChangeParts } from "../lib/proposalOverlay";
import { applySetFieldWrites } from "../lib/setFieldWrite";
import { updateSets } from "../lib/updateSets";
import { MoveParts } from "./MoveParts";

/* THE PROPOSALS ON ONE OPEN DAY — W72 step 2. Next week's copy against the previous copy of the SAME
 * template (`nextWeekPairs`), each moved row in the coach's words with its three actions ON THE ROW:
 * `Keep` (writes nothing — the number is already next week's) · `Change…` (the existing set editor) ·
 * `Hold at 60` (writes this week's value back, one field, exactly the grouped set rows).
 *
 * ⚠️ AFTER A WRITE THE TREES ARE READ AGAIN FROM THE SERVER — never a local copy of the value, which
 * would be two stores of one fact and could show a Hold that the database refused. */
function namesOf(tree: AssignedWorkoutTree | null, into: Record<string, string>): Record<string, string> {
  for (const s of tree?.coach_workout_sections ?? []) {
    for (const ex of s.coach_workout_exercises ?? []) {
      const key = ex.exercise_id ?? ex.id;
      const name = ex.coach_exercises?.name;
      if (key && name && !(key in into)) into[key] = name;
    }
  }
  return into;
}

export function ProposalTiles({ latestId, previousId, onChange, onWritten }: {
  latestId: string;
  previousId: string;
  /** Opens the existing editor on that exercise row of next week's copy. */
  onChange?: (workoutId: string, exerciseRowId: string) => void;
  /** The page re-reads the client after a Hold, so the calendar and the counters agree with the server. */
  onWritten?: () => void;
}) {
  const [state, setState] = useState<
    | { kind: "loading" } | { kind: "failed"; message: string }
    | { kind: "ready"; rows: RowComparison[]; names: Record<string, string> }
  >({ kind: "loading" });
  const [reload, setReload] = useState(0);
  const [kept, setKept] = useState<Set<string>>(new Set());
  const [writeError, setWriteError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const rpc = (fn: never, args: never) => supabase.rpc(fn, args);
      const [latest, previous] = await Promise.all([
        fetchAssignedWorkoutTree(rpc as never, latestId), fetchAssignedWorkoutTree(rpc as never, previousId),
      ]);
      if (cancelled) return;
      if (latest.tree === null) { setState({ kind: "failed", message: latest.error }); return; }
      if (previous.tree === null) { setState({ kind: "failed", message: previous.error }); return; }
      setState({
        kind: "ready",
        rows: compareWeeks(flattenWeek(previous.tree), flattenWeek(latest.tree)),
        names: namesOf(previous.tree, namesOf(latest.tree, {})),
      });
    })();
    return () => { cancelled = true; };
  }, [latestId, previousId, reload]);

  if (state.kind === "loading") return <p className="ht-caption proposal-quiet">Reading next week…</p>;
  if (state.kind === "failed") return <p className="ht-caption proposal-quiet" role="alert">{state.message}</p>;

  /* ⚠️ STEP 2 FIX 2 — THE SMALLEST MARK (Fable): ONE line per moved exercise, `Wrist Circles 1 → 2 reps`, with
     ONLY `→ 2` in the accent; `Keep · Change… · Hold at 1` show on hover / keyboard focus of THAT line, never at
     rest; unchanged exercises are counted in one grey line, never listed (a list of set groups is the set table
     leaking into a calendar cell). */
  const g = groupMoves(state.rows);
  const hold = (write: NonNullable<ReturnType<typeof holdWrite>>) => {
    setWriteError(null);
    void applySetFieldWrites(updateSets, [write]).then(({ error }) => {
      if (error !== null) { setWriteError(`That did not save: ${error}`); return; }
      setReload((n) => n + 1);
      onWritten?.();
    });
  };
  return (
    <ul className="proposal-rows" aria-label="What next week changes">
      {g.moved.map((m) => {
        const name = state.names[m.exerciseId] ?? "Exercise";
        const isKept = kept.has(m.key);
        return (
          <li key={m.key} className="proposal-row is-moved">
            <span className="proposal-line">
              <span className="proposal-name">{name}</span>{" "}
              <MoveParts moves={m.moves} />
              {rowChangeParts(m.addedRows, m.droppedRows).map((s, i) => (
                <span key={`r${i}`} className="proposal-move">{(m.moves.length > 0 || i > 0) && " · "}<span className="proposal-to">{s}</span></span>
              ))}
            </span>
            <span className="proposal-actions">
              <button type="button" className="proposal-action" aria-pressed={isKept}
                      onClick={() => setKept((k) => { const n = new Set(k); if (n.has(m.key)) n.delete(m.key); else n.add(m.key); return n; })}>
                {isKept ? "Kept" : "Keep"}
              </button>
              {onChange !== undefined && m.exerciseRowId !== undefined && (
                <button type="button" className="proposal-action"
                        onClick={() => onChange(latestId, m.exerciseRowId!)}>Change…</button>
              )}
              {m.moves.map((mv, i) => {
                const write = holdWrite(mv.delta, mv.row);
                return canHold(mv.delta, mv.row) && write !== null ? (
                  <button key={`h${i}`} type="button" className="proposal-action" onClick={() => hold(write)}>
                    {holdLabel(mv.delta)}
                  </button>
                ) : null;
              })}
            </span>
          </li>
        );
      })}
      {g.added.map((id) => (
        <li key={`a-${id}`} className="proposal-row is-quiet"><span className="proposal-name">{state.names[id] ?? "Exercise"}</span> new next week</li>
      ))}
      {g.dropped.map((id) => (
        <li key={`d-${id}`} className="proposal-row is-quiet"><span className="proposal-name">{state.names[id] ?? "Exercise"}</span> not in next week</li>
      ))}
      {g.unchanged > 0 && <li className="proposal-row is-quiet">+ {g.unchanged} unchanged</li>}
      {writeError !== null && <li className="proposal-row" role="alert">{writeError}</li>}
    </ul>
  );
}
